<section class="owner-portal-shell">
  <div class="mx-auto w-full max-w-[1800px] px-3 py-4 sm:px-5 sm:py-6 lg:px-8 lg:py-8">
    <div class="space-y-5 lg:space-y-6">
      <header class="portal-hero">
        <div class="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
          <div class="max-w-3xl">
            <div class="eyebrow">
              <span class="status-dot"></span>
              Owner Portal
            </div>
            <h1 class="mt-3 text-3xl font-bold tracking-tight text-text-primary sm:text-4xl">
              {{ 'ownerPortalDashboard.header.title' | translate }}
            </h1>
            <p class="mt-3 max-w-2xl text-sm leading-6 text-text-secondary sm:text-base">
              {{ 'ownerPortalDashboard.header.subtitle' | translate }}
            </p>
          </div>

          <button type="button" (click)="refreshDashboard()" class="btn-primary self-start">
            <i class="fa-solid fa-rotate-right text-xs" [class.fa-spin]="isLoading"></i>
            {{ 'ownerPortalDashboard.header.refresh' | translate }}
          </button>
        </div>

        <div class="mt-7 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <article class="metric-card">
            <div class="metric-icon"><i class="fa-solid fa-building"></i></div>
            <div>
              <p class="metric-label">{{ 'ownerPortalDashboard.header.units' | translate }}</p>
              <p class="metric-value">{{ formatNumber(totalUnits) }}</p>
              <p class="metric-caption">{{ 'ownerPortalDashboard.header.totalUnits' | translate }}</p>
            </div>
          </article>

          <article class="metric-card">
            <div class="metric-icon"><i class="fa-solid fa-calendar-check"></i></div>
            <div>
              <p class="metric-label">{{ 'ownerPortalDashboard.header.bookings' | translate }}</p>
              <p class="metric-value">{{ formatNumber(totalBookings) }}</p>
              <p class="metric-caption">{{ 'ownerPortalDashboard.header.totalBookings' | translate }}</p>
            </div>
          </article>

          <article class="metric-card">
            <div class="metric-icon"><i class="fa-solid fa-wallet"></i></div>
            <div>
              <p class="metric-label">{{ 'ownerPortalDashboard.header.revenue' | translate }}</p>
              <p class="metric-value">{{ formatCurrency(totalRevenue) }}</p>
              <p class="metric-caption">{{ 'ownerPortalDashboard.header.totalRevenue' | translate }}</p>
            </div>
          </article>

          <article class="metric-card">
            <div class="metric-icon"><i class="fa-solid fa-user-shield"></i></div>
            <div>
              <p class="metric-label">{{ 'ownerPortalDashboard.header.role' | translate }}</p>
              <p class="metric-value text-xl">owner_portal</p>
              <p class="metric-caption">{{ 'ownerPortalDashboard.header.managedAccount' | translate }}</p>
            </div>
          </article>
        </div>

        <div *ngIf="isLoading" class="state-banner mt-5">
          <i class="fa-solid fa-circle-notch fa-spin text-primary"></i>
          {{ 'ownerPortalDashboard.header.loading' | translate }}
        </div>
        <div *ngIf="!isLoading && errorMessage" class="state-banner state-banner-danger mt-5">
          <i class="fa-solid fa-triangle-exclamation text-danger"></i>
          {{ errorMessage | translate }}
        </div>
      </header>

      <section class="portal-panel">
        <nav class="tab-toolbar" aria-label="Owner portal sections">
          <button *ngFor="let tab of tabs" type="button" (click)="setActiveTab(tab.id)" class="tab-pill" [ngClass]="{
              'tab-pill-active': activeTab === tab.id,
              'tab-pill-idle': activeTab !== tab.id
            }">
            {{ 'ownerPortalDashboard.tabs.' + tab.id | translate }}
          </button>
        </nav>

        <div class="p-4 sm:p-5 lg:p-6">
          <ng-container [ngSwitch]="activeTab">
            <div *ngSwitchCase="'overview'" class="space-y-6">
              <div class="grid gap-3 md:grid-cols-3">
                <article class="summary-card">
                  <span class="summary-icon"><i class="fa-solid fa-layer-group"></i></span>
                  <p class="summary-label">{{ 'ownerPortalDashboard.overview.units' | translate }}</p>
                  <p class="summary-value">{{ totalUnits }}</p>
                </article>
                <article class="summary-card">
                  <span class="summary-icon"><i class="fa-solid fa-calendar-days"></i></span>
                  <p class="summary-label">{{ 'ownerPortalDashboard.overview.bookings' | translate }}</p>
                  <p class="summary-value">{{ totalBookings }}</p>
                </article>
                <article class="summary-card">
                  <span class="summary-icon"><i class="fa-solid fa-chart-line"></i></span>
                  <p class="summary-label">{{ 'ownerPortalDashboard.overview.revenue' | translate }}</p>
                  <p class="summary-value">{{ formatCurrency(totalRevenue) }}</p>
                </article>
              </div>

              <section class="table-shell">
                <div class="table-header">
                  <div>
                    <p class="eyebrow">{{ 'ownerPortalDashboard.overview.portfolio' | translate }}</p>
                    <h2 class="section-title">{{ 'ownerPortalDashboard.overview.unitsList' | translate }}</h2>
                  </div>

                  <div class="toolbar">
                    <label class="search-box">
                      <i class="fa-solid fa-magnifying-glass text-text-muted"></i>
                      <input type="search" [value]="unitSearchTerm"
                        (input)="setUnitSearchTerm($any($event.target).value)"
                        [placeholder]="'ownerPortalDashboard.overview.searchUnits' | translate"
                        [attr.aria-label]="'ownerPortalDashboard.overview.searchUnits' | translate" />
                    </label>
                    <label class="page-size">
                      <span>{{ 'ownerPortalDashboard.overview.rows' | translate }}</span>
                      <select [value]="unitPageSize" (change)="setUnitPageSize($any($event.target).value)"
                        [attr.aria-label]="'ownerPortalDashboard.overview.rowsPerPage' | translate">
                        <option *ngFor="let size of unitPageSizes" [value]="size">{{ size }}</option>
                      </select>
                    </label>
                  </div>
                </div>

                <div *ngIf="isLoading" class="space-y-2 p-4">
                  <div *ngFor="let item of [1, 2, 3, 4, 5]" class="skeleton-row"></div>
                </div>

                <div *ngIf="!isLoading && errorMessage" class="empty-state">
                  <span class="empty-icon empty-icon-danger"><i class="fa-solid fa-triangle-exclamation"></i></span>
                  <h3>{{ errorMessage | translate }}</h3>
                  <p>{{ 'ownerPortalDashboard.overview.loading' | translate }}</p>
                  <button type="button" (click)="refreshDashboard()" class="btn-ghost">
                    <i class="fa-solid fa-rotate-right text-xs"></i>
                    {{ 'ownerPortalDashboard.header.refresh' | translate }}
                  </button>
                </div>

                <div *ngIf="!isLoading && !errorMessage && sortedUnits.length === 0" class="empty-state">
                  <span class="empty-icon"><i class="fa-solid fa-building-circle-xmark"></i></span>
                  <h3>{{ 'ownerPortalDashboard.overview.noUnits' | translate }}</h3>
                  <p>{{ 'ownerPortalDashboard.overview.refineSearch' | translate }}</p>
                  <button type="button" (click)="refreshDashboard()" class="btn-ghost">
                    <i class="fa-solid fa-rotate-right text-xs"></i>
                    {{ 'ownerPortalDashboard.header.refresh' | translate }}
                  </button>
                </div>

                <div *ngIf="!isLoading && !errorMessage && sortedUnits.length > 0">
                  <div class="hidden lg:block">
                    <div class="overflow-x-auto">
                      <table class="premium-table">
                        <thead>
                          <tr>
                            <th>
                              <button type="button" (click)="sortUnits('title')" class="sort-button"
                                [class.sort-active]="unitSortKey === 'title'">
                                {{ 'ownerPortalDashboard.overview.table.unit' | translate }}
                                <i class="fa-solid" [ngClass]="getSortIcon('title')"></i>
                              </button>
                            </th>
                            <th>
                              <button type="button" (click)="sortUnits('status')" class="sort-button"
                                [class.sort-active]="unitSortKey === 'status'">
                                {{ 'ownerPortalDashboard.overview.table.status' | translate }}
                                <i class="fa-solid" [ngClass]="getSortIcon('status')"></i>
                              </button>
                            </th>
                            <th>
                              <button type="button" (click)="sortUnits('purpose')" class="sort-button"
                                [class.sort-active]="unitSortKey === 'purpose'">
                                {{ 'ownerPortalDashboard.overview.table.purpose' | translate }}
                                <i class="fa-solid" [ngClass]="getSortIcon('purpose')"></i>
                              </button>
                            </th>
                            <th>{{ 'ownerPortalDashboard.overview.table.specs' | translate }}</th>
                            <th>
                              <button type="button" (click)="sortUnits('price')" class="sort-button"
                                [class.sort-active]="unitSortKey === 'price'">
                                {{ 'ownerPortalDashboard.overview.table.price' | translate }}
                                <i class="fa-solid" [ngClass]="getSortIcon('price')"></i>
                              </button>
                            </th>
                            <th>
                              <button type="button" (click)="sortUnits('updatedAt')" class="sort-button"
                                [class.sort-active]="unitSortKey === 'updatedAt'">
                                {{ 'ownerPortalDashboard.overview.table.updated' | translate }}
                                <i class="fa-solid" [ngClass]="getSortIcon('updatedAt')"></i>
                              </button>
                            </th>
                            <th class="text-right">{{ 'ownerPortalDashboard.overview.table.actions' | translate }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr *ngFor="let unit of paginatedUnits; trackBy: trackByUnit" (click)="selectUnit(unit)"
                            [class.row-selected]="isUnitSelected(unit)" tabindex="0">
                            <td>
                              <div class="flex items-center gap-3">
                                <div class="unit-thumb">
                                  <img *ngIf="unit.gallery?.[0]?.secure_url" [src]="unit.gallery![0].secure_url"
                                    [alt]="getUnitTitle(unit)" />
                                  <i *ngIf="!unit.gallery?.[0]?.secure_url" class="fa-solid fa-building"></i>
                                </div>
                                <div class="min-w-0">
                                  <p class="cell-primary truncate">{{ getUnitTitle(unit) }}</p>
                                  <p class="cell-secondary truncate">
                                    {{ unit.unitNumber || unit.unitType || unit._id }}
                                  </p>
                                  <p *ngIf="getUnitLocation(unit)" class="cell-meta truncate">
                                    {{ getUnitLocation(unit) }}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span class="status-badge" [ngClass]="{
                                  'badge-success': unit.status === 'active',
                                  'badge-warning': unit.status === 'pending',
                                  'badge-danger': unit.status === 'inactive',
                                  'badge-muted': !['active','pending','inactive'].includes(unit.status ?? '')
                                }">
                                {{ getUnitStatusKey(unit.status) | translate }}
                              </span>
                            </td>
                            <td>
                              <p class="cell-primary capitalize">{{ getUnitPurposeKey(unit.purpose) | translate }}</p>
                              <p class="cell-secondary">{{ getUnitType(unit) || '-' }}</p>
                            </td>
                            <td>
                              <div class="meta-pills">
                                <span>{{ unit.unitArea || 0 }} {{ 'ownerPortalDashboard.overview.sqm' | translate
                                  }}</span>
                                <span>{{ unit.bedrooms || 0 }} {{ 'ownerPortalDashboard.overview.rooms' | translate
                                  }}</span>
                                <span>{{ unit.bathrooms || 0 }} {{ 'ownerPortalDashboard.overview.baths' | translate
                                  }}</span>
                              </div>
                            </td>
                            <td>
                              <p class="cell-primary">{{ getUnitPrice(unit) }}</p>
                              <p *ngIf="unit.furnished" class="cell-meta">
                                {{ 'ownerPortalDashboard.overview.furnished' | translate }}
                              </p>
                            </td>
                            <td>
                              <p class="cell-secondary">{{ unit.updatedAt ? (unit.updatedAt | date:'yyyy-MM-dd') : '-'
                                }}</p>
                            </td>
                            <td>
                              <div class="flex justify-end gap-2">
                                <button type="button" (click)="$event.stopPropagation(); openEditUnit(unit)"
                                  class="icon-button"
                                  [attr.aria-label]="'ownerPortalDashboard.overview.editUnit' | translate">
                                  <i class="fa-solid fa-pen"></i>
                                </button>
                                <button type="button" (click)="$event.stopPropagation(); openPricing(unit)"
                                  class="icon-button" [attr.aria-label]="'ownerPortalDashboard.overview.pricing' | translate">
                                  <i class="fa-solid fa-tags"></i>
                                </button>
                                <button type="button" (click)="$event.stopPropagation(); openAvailabilityPanel(unit)"
                                  class="icon-button"
                                  [attr.aria-label]="'ownerPortalDashboard.overview.availability' | translate">
                                  <i class="fa-solid fa-calendar-days"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div class="space-y-3 p-4 lg:hidden">
                    <article *ngFor="let unit of paginatedUnits; trackBy: trackByUnit" class="mobile-unit-card"
                      [class.row-selected]="isUnitSelected(unit)" (click)="selectUnit(unit)">
                      <div class="flex gap-3">
                        <div class="unit-thumb unit-thumb-lg">
                          <img *ngIf="unit.gallery?.[0]?.secure_url" [src]="unit.gallery![0].secure_url"
                            [alt]="getUnitTitle(unit)" />
                          <i *ngIf="!unit.gallery?.[0]?.secure_url" class="fa-solid fa-building"></i>
                        </div>
                        <div class="min-w-0 flex-1">
                          <div class="flex items-start justify-between gap-3">
                            <div class="min-w-0">
                              <p class="cell-primary truncate">{{ getUnitTitle(unit) }}</p>
                              <p class="cell-secondary truncate">{{ getUnitLocation(unit) || unit.unitType || unit._id
                                }}</p>
                            </div>
                            <span class="status-badge shrink-0" [ngClass]="{
                                'badge-success': unit.status === 'active',
                                'badge-warning': unit.status === 'pending',
                                'badge-danger': unit.status === 'inactive',
                                'badge-muted': !['active','pending','inactive'].includes(unit.status ?? '')
                              }">
                              {{ getUnitStatusKey(unit.status) | translate }}
                            </span>
                          </div>
                          <div class="mt-3 flex flex-wrap items-center gap-2">
                            <span class="mobile-meta">{{ getUnitPurposeKey(unit.purpose) | translate }}</span>
                            <span class="mobile-meta">{{ unit.unitArea || 0 }} {{ 'ownerPortalDashboard.overview.sqm' |
                              translate }}</span>
                            <span class="mobile-meta">{{ getUnitPrice(unit) }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="mt-4 grid grid-cols-3 gap-2">
                        <button type="button" (click)="$event.stopPropagation(); openEditUnit(unit)"
                          class="btn-ghost compact">
                          <i class="fa-solid fa-pen"></i>
                          {{ 'ownerPortalDashboard.overview.editUnit' | translate }}
                        </button>
                        <button type="button" (click)="$event.stopPropagation(); openPricing(unit)"
                          class="btn-ghost compact">
                          <i class="fa-solid fa-tags"></i>
                          {{ 'ownerPortalDashboard.overview.pricing' | translate }}
                        </button>
                        <button type="button" (click)="$event.stopPropagation(); openAvailabilityPanel(unit)"
                          class="btn-ghost compact">
                          <i class="fa-solid fa-calendar-days"></i>
                          {{ 'ownerPortalDashboard.overview.availability' | translate }}
                        </button>
                      </div>
                    </article>
                  </div>

                  <div class="pagination-bar">
                    <p class="text-xs font-medium text-text-muted">
                      {{ 'ownerPortalDashboard.overview.showing' | translate }} {{ unitPageStart }}-{{ unitPageEnd }}
                      {{ 'ownerPortalDashboard.overview.of' | translate }} {{ sortedUnits.length }}
                    </p>
                    <div class="flex items-center gap-1.5">
                      <button type="button" class="page-button" [disabled]="normalizedUnitPage === 1"
                        (click)="setUnitPage(normalizedUnitPage - 1)">
                        <i class="fa-solid fa-chevron-left text-[10px]"></i>
                      </button>
                      <button *ngFor="let page of unitPageNumbers" type="button" class="page-button"
                        [class.page-button-active]="normalizedUnitPage === page" (click)="setUnitPage(page)">
                        {{ page }}
                      </button>
                      <button type="button" class="page-button" [disabled]="normalizedUnitPage === unitTotalPages"
                        (click)="setUnitPage(normalizedUnitPage + 1)">
                        <i class="fa-solid fa-chevron-right text-[10px]"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            <div *ngSwitchCase="'profile'" class="space-y-6">
              <section class="form-panel">
                <div class="section-heading">
                  <div>
                    <p class="eyebrow">Account</p>
                    <h2 class="section-title">{{ 'ownerPortalDashboard.profile.title' | translate }}</h2>
                    <p class="section-subtitle">{{ 'ownerPortalDashboard.profile.subtitle' | translate }}</p>
                  </div>
                </div>

                <form class="mt-6 grid gap-4 md:grid-cols-2" [formGroup]="profileForm" (ngSubmit)="saveProfile()">
                  <div class="md:col-span-2 flex items-center gap-5 rounded-2xl border border-border bg-surface p-4">
                    <img [src]="profileImagePreview || profile?.profileImage || 'assets/default-avatar.png'"
                      alt="Profile Image"
                      class="h-20 w-20 rounded-2xl border border-border-strong bg-surface-active object-cover" />
                    <div class="min-w-0">
                      <p class="text-sm font-semibold text-text-primary">{{ profile?.firstName }} {{ profile?.lastName
                        }}</p>
                      <p class="mt-1 truncate text-xs text-text-muted">{{ profile?.email }}</p>
                      <p class="text-xs font-medium uppercase text-primary">{{ profile?.role }}</p>
                    </div>
                  </div>

                  <label class="field">
                    <span>{{ 'ownerPortalDashboard.profile.firstName' | translate }}</span>
                    <input formControlName="firstName" class="field-control" />
                  </label>
                  <label class="field">
                    <span>{{ 'ownerPortalDashboard.profile.lastName' | translate }}</span>
                    <input formControlName="lastName" class="field-control" />
                  </label>
                  <label class="field">
                    <span>{{ 'ownerPortalDashboard.profile.phoneNumber' | translate }}</span>
                    <input formControlName="phoneNumber" class="field-control" />
                  </label>
                  <label class="field">
                    <span>{{ 'ownerPortalDashboard.profile.language' | translate }}</span>
                    <select formControlName="lang" class="field-control uppercase">
                      <option *ngFor="let l of langs" [value]="l">{{ l }}</option>
                    </select>
                  </label>
                  <label class="field">
                    <span>{{ 'ownerPortalDashboard.profile.profileVisibility' | translate }}</span>
                    <select formControlName="showProfile" class="field-control capitalize">
                      <option *ngFor="let opt of showProfileOptions" [value]="opt">{{ opt }}</option>
                    </select>
                  </label>
                  <label class="field">
                    <span>{{ 'ownerPortalDashboard.profile.numberOfPropertiesOwned' | translate }}</span>
                    <input formControlName="numberOfPropertiesOwned" type="number" min="0" class="field-control" />
                  </label>
                  <label class="field md:col-span-2">
                    <span>{{ 'ownerPortalDashboard.profile.aboutMe' | translate }}</span>
                    <textarea formControlName="aboutMe" rows="4" class="field-control resize-none"></textarea>
                  </label>
                  <label class="field md:col-span-2">
                    <span>{{ 'ownerPortalDashboard.profile.location' | translate }}</span>
                    <input formControlName="location" class="field-control" />
                  </label>

                  <div class="md:col-span-2 rounded-2xl border border-border bg-surface p-4">
                    <span class="mb-3 block text-sm font-semibold text-text-primary">
                      {{ 'ownerPortalDashboard.profile.notifications' | translate }}
                    </span>
                    <div class="flex flex-wrap gap-4">
                      <label class="checkbox-field">
                        <input type="checkbox" formControlName="notificationEmail" />
                        <span>{{ 'ownerPortalDashboard.profile.notificationEmail' | translate }}</span>
                      </label>
                      <label class="checkbox-field">
                        <input type="checkbox" formControlName="notificationApp" />
                        <span>{{ 'ownerPortalDashboard.profile.notificationApp' | translate }}</span>
                      </label>
                    </div>
                  </div>

                  <label class="field md:col-span-2">
                    <span>{{ 'ownerPortalDashboard.profile.attachment' | translate }}</span>
                    <input type="file" accept="image/jpeg,image/png,image/webp" (change)="onFileSelected($event)"
                      class="file-control" [disabled]="!isEditingProfile" />
                    <img *ngIf="profileImagePreview" [src]="profileImagePreview" alt="Preview"
                      class="mt-3 h-20 w-20 rounded-2xl border border-border object-cover" />
                  </label>

                  <div class="md:col-span-2 flex flex-wrap items-center gap-3">
                    <button type="button" *ngIf="!isEditingProfile" (click)="toggleEditProfile()" class="btn-primary">
                      <i class="fa-solid fa-pen text-xs"></i>
                      {{ 'ownerPortalDashboard.profile.edit' | translate }}
                    </button>

                    <button type="submit" *ngIf="isEditingProfile" [disabled]="saving || profileForm.invalid" class="btn-primary">
                      <i class="fa-solid fa-floppy-disk text-xs"></i>
                      {{ saving ? ('ownerPortalDashboard.profile.saving' | translate) :
                      ('ownerPortalDashboard.profile.save' | translate) }}
                    </button>

                    <button type="button" *ngIf="isEditingProfile" (click)="cancelEditProfile()" [disabled]="saving" class="btn-secondary flex items-center gap-2 rounded-xl border border-border bg-surface px-4 py-2 text-sm font-semibold text-text-primary transition-colors hover:bg-surface-active disabled:cursor-not-allowed disabled:opacity-50">
                      <i class="fa-solid fa-xmark text-xs"></i>
                      {{ 'ownerPortalDashboard.profile.cancel' | translate }}
                    </button>

                    <div *ngIf="successMessage" class="state-inline state-success">
                      {{ successMessage | translate }}
                    </div>
                    <div *ngIf="errorMessage" class="state-inline state-danger">
                      {{ errorMessage | translate }}
                    </div>
                  </div>
                </form>
              </section>
            </div>

            <div *ngSwitchCase="'wallet'" class="space-y-6">
              <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <article class="summary-card">
                  <span class="summary-icon"><i class="fa-solid fa-vault"></i></span>
                  <p class="summary-label">{{ 'ownerPortalDashboard.wallet.balance' | translate }}</p>
                  <p class="summary-value">{{ formatCurrency(wallet?.balance ?? 0) }}</p>
                </article>
                <article class="summary-card">
                  <span class="summary-icon"><i class="fa-solid fa-arrow-trend-up"></i></span>
                  <p class="summary-label">{{ 'ownerPortalDashboard.wallet.totalRevenue' | translate }}</p>
                  <p class="summary-value">{{ formatCurrency(wallet?.totalRevenue ?? 0) }}</p>
                </article>
                <article class="summary-card">
                  <span class="summary-icon"><i class="fa-solid fa-receipt"></i></span>
                  <p class="summary-label">{{ 'ownerPortalDashboard.wallet.totalDeductions' | translate }}</p>
                  <p class="summary-value">{{ formatCurrency(wallet?.totalDeductions ?? 0) }}</p>
                </article>
                <article class="summary-card">
                  <span class="summary-icon"><i class="fa-solid fa-money-bill-transfer"></i></span>
                  <p class="summary-label">{{ 'ownerPortalDashboard.wallet.withdrawn' | translate }}</p>
                  <p class="summary-value">{{ formatCurrency(wallet?.withdrawn ?? 0) }}</p>
                </article>
              </div>

              <section class="form-panel">
                <div class="section-heading">
                  <div>
                    <p class="eyebrow">Payouts</p>
                    <h2 class="section-title">{{ 'ownerPortalDashboard.wallet.requestPayout' | translate }}</h2>
                  </div>
                </div>
                <form class="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end" [formGroup]="payoutForm"
                  (ngSubmit)="requestPayout()">
                  <label class="field flex-1">
                    <span>{{ 'ownerPortalDashboard.wallet.amount' | translate }}</span>
                    <input formControlName="amount" type="number" min="1" class="field-control" />
                  </label>
                  <button type="submit" [disabled]="submitting" class="btn-primary">
                    <i class="fa-solid fa-paper-plane text-xs"></i>
                    {{ submitting ? ('ownerPortalDashboard.wallet.requesting' | translate) :
                    ('ownerPortalDashboard.wallet.request' | translate) }}
                  </button>
                </form>
                <div *ngIf="payoutMessage" class="state-inline state-success mt-3">{{ payoutMessage | translate }}</div>
                <div *ngIf="errorMessage" class="state-inline state-danger mt-3">{{ errorMessage | translate }}</div>
              </section>

              <section class="table-shell">
                <div class="table-header">
                  <div>
                    <p class="eyebrow">Ledger</p>
                    <h2 class="section-title">{{ 'ownerPortalDashboard.wallet.transactions' | translate }}</h2>
                  </div>
                </div>

                <div *ngIf="wallet?.transactions?.length === 0" class="empty-state">
                  <span class="empty-icon"><i class="fa-solid fa-file-invoice-dollar"></i></span>
                  <h3>{{ 'ownerPortalDashboard.wallet.noTransactions' | translate }}</h3>
                  <p>Wallet activity will appear here when transactions are available.</p>
                </div>

                <div *ngIf="(wallet?.transactions?.length ?? 0) > 0" class="overflow-x-auto">
                  <table class="premium-table">
                    <thead>
                      <tr>
                        <th>Transaction</th>
                        <th>Type</th>
                        <th>Date</th>
                        <th class="text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let item of wallet?.transactions ?? []; trackBy: trackByTransaction">
                        <td>
                          <p class="cell-primary">{{ item.description || item.type }}</p>
                          <p class="cell-secondary">{{ item.referenceId || item._id }}</p>
                        </td>
                        <td>
                          <span class="status-badge badge-muted">{{ item.type }}</span>
                        </td>
                        <td class="cell-secondary">{{ item.createdAt | date:'yyyy-MM-dd HH:mm' }}</td>
                        <td class="text-right font-semibold"
                          [ngClass]="item.amount >= 0 ? 'text-success' : 'text-danger'">
                          {{ formatCurrency(item.amount) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
            </div>

            <div *ngSwitchCase="'marketing'" class="space-y-6">
              <app-marketing></app-marketing>
            </div>

            <div *ngSwitchCase="'add-unit'" class="space-y-6">
              <section class="form-panel">
                <div class="section-heading mb-6">
                  <div>
                    <p class="eyebrow">Inventory</p>
                    <h2 class="section-title">{{ 'ownerPortalDashboard.addUnit.title' | translate }}</h2>
                    <p class="section-subtitle">{{ 'ownerPortalDashboard.addUnit.subtitle' | translate }}</p>
                  </div>
                </div>
                <app-add-new-property></app-add-new-property>
              </section>
            </div>
          </ng-container>
        </div>
      </section>
    </div>
  </div>
</section>

@if (pricingUnitId) {
<app-seasonal-pricing-panel [unitId]="pricingUnitId" (closed)="closePricing()" />
}

@if (availabilityUnitId) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
  (click)="closeAvailabilityPanel()">
  <div
    class="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl border border-border bg-surface-elevated shadow-elevated"
    (click)="$event.stopPropagation()">
    <div class="flex shrink-0 items-center justify-between border-b border-border px-5 py-4">
      <div class="flex items-center gap-3">
        <div
          class="flex h-9 w-9 items-center justify-center rounded-xl border border-border-primary bg-primary-soft text-primary">
          <i class="fa-solid fa-calendar-days text-sm"></i>
        </div>
        <h2 class="text-sm font-bold tracking-tight text-text-primary">{{ 'ownerPortalDashboard.overview.availability' |
          translate }}</h2>
      </div>
      <button type="button" (click)="closeAvailabilityPanel()" class="icon-button">
        <i class="fa-solid fa-xmark text-sm"></i>
      </button>
    </div>
    <div class="flex-1 overflow-y-auto p-5">
      <app-availability [unitId]="availabilityUnitId" />
    </div>
  </div>
</div>
}
